/**
 * VehicleSystem — Party vehicle management for world map travel.
 *
 * Vehicles provide speed multipliers and carry capacity bonuses.
 * Each vehicle has a chance to break down on difficult terrain and
 * requires gold to repair.
 *
 * @module VehicleSystem
 */

import DebugLog from '../debug/DebugLog.js';
import WorldState from '../world/WorldState.js';

// ─── Breakdown probabilities by terrain ──────────────────────────────────────

const BREAKDOWN_CHANCE = {
  mountains: 0.15,
  mountain:  0.15,
  swamp:     0.10,
};
const BREAKDOWN_CHANCE_DEFAULT = 0.02;

// ─── VehicleSystem ────────────────────────────────────────────────────────────

const VehicleSystem = {
  /**
   * Vehicle type definitions.
   * speedMult: multiplies party movement speed on the world map.
   * carryBonus: fraction of base carry weight added to party max carry.
   * cost: purchase price in gold.
   * repairCost: gold to repair a broken vehicle.
   */
  VEHICLES: {
    horse: {
      label: 'Horse',
      speedMult: 1.2,
      carryBonus: 0,
      cost: 200,
      repairCost: 20,
    },
    donkey: {
      label: 'Donkey',
      speedMult: 1.0,
      carryBonus: 0.3,
      cost: 80,
      repairCost: 10,
    },
    wagon: {
      label: 'Wagon',
      speedMult: 0.9,
      carryBonus: 1.0,
      cost: 350,
      repairCost: 50,
    },
  },

  /**
   * Array of vehicle instances currently owned by the party.
   * Each instance: { id: string, vehicleId: string, broken: boolean }
   * @type {Array<{ id: string, vehicleId: string, broken: boolean }>}
   */
  _partyVehicles: [],

  // ─── Management ──────────────────────────────────────────────────────────

  /**
   * Add a vehicle to the party.
   * Does not deduct gold — purchasing is handled by the calling system.
   * @param {string} vehicleId — e.g. 'horse', 'donkey', 'wagon'
   * @returns {{ success: boolean, message: string, instance: object|null }}
   */
  addVehicle(vehicleId) {
    const def = this.VEHICLES[vehicleId];
    if (!def) {
      const valid = Object.keys(this.VEHICLES).join(', ');
      return { success: false, message: `Unknown vehicle "${vehicleId}". Valid: ${valid}`, instance: null };
    }

    const instance = {
      id: `vehicle_${vehicleId}_${Date.now().toString(36)}`,
      vehicleId,
      broken: false,
    };

    this._partyVehicles.push(instance);

    DebugLog.info(
      `[vehicle] Vehicle added; vehicleId=${vehicleId} label="${def.label}" ` +
      `instanceId=${instance.id} total=${this._partyVehicles.length}`
    );

    return { success: true, message: `Added ${def.label} to party.`, instance };
  },

  /**
   * Remove a vehicle instance by instance ID.
   * @param {string} instanceId — The instance's `id` field
   * @returns {{ success: boolean, message: string }}
   */
  removeVehicle(instanceId) {
    const idx = this._partyVehicles.findIndex(v => v.id === instanceId);
    if (idx === -1) {
      return { success: false, message: `Vehicle instance "${instanceId}" not found.` };
    }

    const [removed] = this._partyVehicles.splice(idx, 1);
    const def = this.VEHICLES[removed.vehicleId];

    DebugLog.info(
      `[vehicle] Vehicle removed; instanceId=${instanceId} vehicleId=${removed.vehicleId} ` +
      `label="${def?.label ?? removed.vehicleId}" total=${this._partyVehicles.length}`
    );

    return { success: true, message: `Removed ${def?.label ?? removed.vehicleId}.` };
  },

  // ─── Carry & speed bonuses ────────────────────────────────────────────────

  /**
   * Get total carry capacity bonus from all non-broken vehicles.
   * The bonus is a multiplier added to the base party carry weight.
   * A wagon with carryBonus=1.0 doubles the carry weight.
   * @returns {number} Total carry bonus (additive sum of carryBonus values)
   */
  getCarryBonus() {
    return this._partyVehicles
      .filter(v => !v.broken)
      .reduce((sum, v) => sum + (this.VEHICLES[v.vehicleId]?.carryBonus ?? 0), 0);
  },

  /**
   * Get movement speed multiplier from the best single non-broken vehicle.
   * Only the highest speedMult among all non-broken vehicles applies.
   * Returns 1.0 if no non-broken vehicles.
   * @returns {number}
   */
  getSpeedMult() {
    const functional = this._partyVehicles.filter(v => !v.broken);
    if (functional.length === 0) return 1.0;

    return functional.reduce((best, v) => {
      const mult = this.VEHICLES[v.vehicleId]?.speedMult ?? 1.0;
      return mult > best ? mult : best;
    }, 1.0);
  },

  // ─── Breakdown ───────────────────────────────────────────────────────────

  /**
   * Roll for vehicle breakdown on difficult terrain.
   * Only non-broken vehicles are at risk.
   * If multiple vehicles are present, each is rolled independently;
   * this method returns the result for the first vehicle that breaks down.
   * @param {string} terrain — e.g. 'mountains', 'swamp', 'forest'
   * @param {object} rng — RNG instance
   * @returns {{ breakdown: boolean, vehicleId: string|null, instanceId: string|null }}
   */
  checkBreakdown(terrain, rng) {
    const functional = this._partyVehicles.filter(v => !v.broken);
    if (functional.length === 0) {
      return { breakdown: false, vehicleId: null, instanceId: null };
    }

    const chance = BREAKDOWN_CHANCE[terrain] ?? BREAKDOWN_CHANCE_DEFAULT;

    for (const instance of functional) {
      const roll = rng?.nextFloat?.() ?? Math.random();
      if (roll < chance) {
        instance.broken = true;
        const def = this.VEHICLES[instance.vehicleId];

        DebugLog.warn(
          `[vehicle] Breakdown! instanceId=${instance.id} vehicleId=${instance.vehicleId} ` +
          `terrain=${terrain} chance=${chance} roll=${roll.toFixed(3)}`
        );

        return {
          breakdown:  true,
          vehicleId:  instance.vehicleId,
          instanceId: instance.id,
          label:      def?.label ?? instance.vehicleId,
          repairCost: def?.repairCost ?? 0,
        };
      }
    }

    return { breakdown: false, vehicleId: null, instanceId: null };
  },

  // ─── Repair ──────────────────────────────────────────────────────────────

  /**
   * Repair a broken vehicle. Deducts repair cost from WorldState.partyGold.
   * @param {string} instanceId — The instance's `id` field
   * @returns {{ success: boolean, message: string, goldSpent: number }}
   */
  repair(instanceId) {
    const instance = this._partyVehicles.find(v => v.id === instanceId);
    if (!instance) {
      return { success: false, message: `Vehicle "${instanceId}" not found.`, goldSpent: 0 };
    }
    if (!instance.broken) {
      return { success: false, message: 'Vehicle is not broken.', goldSpent: 0 };
    }

    const def = this.VEHICLES[instance.vehicleId];
    const cost = def?.repairCost ?? 0;
    const available = WorldState.partyGold ?? 0;

    if (cost > available) {
      return {
        success: false,
        message: `Not enough gold to repair. Need ${cost} gold, have ${available}.`,
        goldSpent: 0,
      };
    }

    WorldState.partyGold -= cost;
    instance.broken = false;

    DebugLog.info(
      `[vehicle] Repaired; instanceId=${instanceId} vehicleId=${instance.vehicleId} ` +
      `cost=${cost} goldRemaining=${WorldState.partyGold}`
    );

    return {
      success: true,
      message: `${def?.label ?? instance.vehicleId} repaired for ${cost} gold.`,
      goldSpent: cost,
    };
  },

  // ─── Accessors ────────────────────────────────────────────────────────────

  /**
   * Get all vehicle instances currently owned by the party.
   * @returns {Array<{ id: string, vehicleId: string, broken: boolean }>}
   */
  getVehicles() {
    return this._partyVehicles;
  },

  // ─── Serialization ────────────────────────────────────────────────────────

  /**
   * Serialize vehicle state for save system.
   * @returns {{ vehicles: Array }}
   */
  serialize() {
    return { vehicles: this._partyVehicles.map(v => ({ ...v })) };
  },

  /**
   * Restore vehicle state from save data.
   * @param {object} data
   */
  deserialize(data) {
    this._partyVehicles = Array.isArray(data?.vehicles)
      ? data.vehicles.map(v => ({ ...v }))
      : [];
    DebugLog.info(`[vehicle] VehicleSystem deserialized; count=${this._partyVehicles.length}`);
  },
};

// ─── Debug exposure ───────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__vehicleSystem = VehicleSystem;
}

export default VehicleSystem;
